import { motion } from "framer-motion";
import { X, Check, Minus } from "lucide-react";
import SectionContainer from "../components/SectionContainer";

const CRITERIA = [
  "Pre-call account briefs",
  "Real-time buying signal detection",
  "Objection and risk alerts during call",
  "CRM auto-update after call",
  "Personalized follow-up drafting",
  "Action items executed in Jira / Linear",
  "Meeting memory across all prior calls",
  "Adapts to your writing style",
];

const COLUMNS = [
  {
    id: "notetaker",
    label: "AI Note-Taker",
    description: "Transcribes and summarizes your meetings",
    highlight: false,
    values: [false, false, false, false, "partial", false, false, false],
  },
  {
    id: "copilot",
    label: "Sales Copilot",
    description: "Answers questions and suggests talk tracks during calls",
    highlight: false,
    values: [false, "partial", "partial", false, false, false, false, false],
  },
  {
    id: "shadow",
    label: "Shadow",
    description: "Revenue AI OS — memory, intelligence, and execution",
    highlight: true,
    values: [true, true, true, true, true, true, true, true],
  },
];

function CellIcon({ value }) {
  if (value === true)      return <Check className="h-4 w-4 text-emerald-500 mx-auto" strokeWidth={3} />;
  if (value === false)     return <X     className="h-4 w-4 text-neutral-400 mx-auto" strokeWidth={2.5} />;
  if (value === "partial") return <Minus className="h-4 w-4 text-amber-500 mx-auto"   strokeWidth={3}   />;
  return null;
}

export default function DifferentiationSection() {
  return (
    <SectionContainer id="differentiation" className="py-20 sm:py-28">
      <div className="mx-auto max-w-3xl text-center mb-14">
        <p className="text-sm font-semibold uppercase tracking-widest text-[#0092B4] mb-3">Why Shadow</p>
        <h2 className="text-[clamp(1.8rem,4vw,2.85rem)] font-bold tracking-tight text-neutral-900">
          Not a note-taker.{" "}
          <span className="text-[#0092B4]">Not a copilot.</span>
          {" "}A Revenue OS.
        </h2>
        <p className="mt-4 text-base text-neutral-500 max-w-xl mx-auto">
          Shadow gives reps live intelligence during calls and executes every action item after —
          two things no note-taker or copilot does together.
        </p>
      </div>

      {/* Comparison table */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm"
      >
        {/* Header row */}
        <div className="grid grid-cols-4 border-b border-neutral-100">
          <div className="p-4 sm:p-5" />
          {COLUMNS.map(col => (
            <div key={col.id} className={`p-4 sm:p-5 text-center ${col.highlight ? "bg-[#0092B4] text-white" : ""}`}>
              <p className={`text-[13px] font-semibold ${col.highlight ? "text-white" : "text-neutral-900"}`}>{col.label}</p>
              <p className={`mt-1 text-[11px] leading-tight hidden sm:block ${col.highlight ? "text-white/75" : "text-neutral-400"}`}>
                {col.description}
              </p>
            </div>
          ))}
        </div>

        {/* Criteria rows */}
        {CRITERIA.map((criterion, rowIdx) => (
          <div
            key={criterion}
            className={`grid grid-cols-4 border-b border-neutral-100 last:border-0 ${
              rowIdx % 2 === 1 ? "bg-neutral-50/50" : ""
            }`}
          >
            <div className="p-3 sm:p-4 flex items-center">
              <span className="text-[12px] sm:text-[13px] text-neutral-800 font-medium">{criterion}</span>
            </div>
            {COLUMNS.map(col => (
              <div
                key={col.id}
                className={`p-3 sm:p-4 flex items-center justify-center ${
                  col.highlight
                    ? "bg-[#0092B4]/[0.07] border-l border-r border-[#0092B4]/20"
                    : ""
                }`}
              >
                <CellIcon value={col.values[rowIdx]} />
              </div>
            ))}
          </div>
        ))}
      </motion.div>

      <p className="mt-4 text-center text-[11px] text-neutral-400">
        Partial support (—) indicates limited or manual implementation
      </p>
    </SectionContainer>
  );
}
